import { Account } from '@/domain/usecases'
import { RouteObject } from 'react-router-dom'

// Layout components
import LoggedLayout from '@/presentation/components/ui/layout/logged-layout'
import BasicLayout from '@/presentation/components/ui/layout/basic-layout'

// Domain models for dynamic routes
import { Category } from '@/domain/models/content/category'
import { EnrollmentCategory } from '@/domain/models/content/enrollment-category'

// Page factories
import * as Pages from './pages'

// Page data loaders
import * as Loaders from './data-loaders'

// Utilities
import { PathConstants } from './paths'
import { guards } from './helpers/guards'
import { createLoaderFactory, LoaderContext } from '@/main/routes/helpers/loaders'
import { RecaptchaProvider } from '@/presentation/contexts/recaptcha/recaptcha-provider'

// ============================================
// AUTHENTICATED ROUTES BUILDER
// ============================================

/**
 * All authenticated routes
 * These routes require user authentication
 *
 * @returns Array of RouteObjects for React Router
 */
export const AuthenticatedRoutes = (account: Account.Model): RouteObject[] => {
  const routeLoader = createLoaderFactory(account)

  const routes = [
    // ========================================
    // LOGOUT ROUTE (no layout - renders only spinner)
    // ========================================
    {
      path: PathConstants.Routes.LOGOUT.value,
      element: <Pages.LogoutPage />
    },

    // ========================================
    // HOME ROUTE and INITIAL LOGGED LAYOUT
    // ========================================
    {
      element: <LoggedLayout navMenu="main" footerVariant="full" enableSearch={true} />,
      children: [
        {
          path: PathConstants.Routes.HOME.value,
          id: PathConstants.Routes.HOME.name,
          element: <Pages.HomePage />,
          loader: routeLoader(guards.passPlanHolder, () => Loaders.loadHome())
        }
      ]
    },

    // ========================================
    // ONBOARDING ROUTES
    // ========================================
    {
      element: <Pages.OnboardingLayout />,
      loader: routeLoader(guards.onboardingAccess, () => Loaders.loadOnboardingMainLayout()),
      children: [
        {
          path: PathConstants.Routes.ONBOARDING.value,
          element: <Pages.OnboardingWelcomePage />,
          loader: routeLoader(guards.onboardingAccess, null)
        },
        {
          path: PathConstants.Routes.ONBOARDING_PERSONAL_DATA.value,
          element: <Pages.OnboardingPersonalDataPage />,
          loader: routeLoader(guards.onboardingAccess, () => Loaders.loadOnboardingStepOptions(1))
        },
        {
          path: PathConstants.Routes.ONBOARDING_2.value,
          element: <Pages.Onboarding2Page />,
          loader: routeLoader(guards.onboardingAccess, null)
        },
        {
          path: PathConstants.Routes.ONBOARDING_PROFESSIONAL_TRAJECTORY.value,
          element: <Pages.OnboardingProfessionalTrajectoryPage />,
          loader: routeLoader(guards.onboardingAccess, () => Loaders.loadOnboardingStepOptions(1))
        },
        {
          path: PathConstants.Routes.ONBOARDING_3.value,
          element: <Pages.Onboarding3Page />,
          loader: routeLoader(guards.onboardingAccess, () => Loaders.loadOnboardingStepOptions(3))
        },
        {
          path: PathConstants.Routes.ONBOARDING_4.value,
          element: <Pages.Onboarding4Page />,
          loader: routeLoader(guards.onboardingAccess, () => Loaders.loadOnboardingStep4())
        },
        {
          path: PathConstants.Routes.ONBOARDING_SECURITY.value,
          element: <Pages.OnboardingSecurityPage />,
          loader: routeLoader(guards.onboardingAccess, null)
        },
        {
          path: PathConstants.Routes.ONBOARDING_COMPLETED.value,
          element: <Pages.OnboardingCompletedPage />,
          loader: routeLoader(guards.onboardingAccess, null)
        }
      ]
    },

    // ========================================
    // USER PROFILE ROUTES
    // Layout: LoggedLayout (navMenu="main", footerVariant="compact", enableSearch=true)
    // ========================================
    {
      element: <LoggedLayout navMenu="main" footerVariant="compact" enableSearch={true} />,
      children: [
        {
          path: PathConstants.Routes.USER_PROFILE.value,
          element: <Pages.UserProfilePage />,
          loader: routeLoader(guards.passPlanHolder, () => Loaders.loadUserProfile(account))
        },
        {
          path: PathConstants.Routes.USER_INSTEREST_AREAS_SKILLS.value,
          element: <Pages.UserInterestAreasSkillsPage />,
          loader: routeLoader(guards.passPlanHolder, () => Loaders.loadUserInterestAreasSkills())
        },
        {
          path: PathConstants.Routes.USER_SETTINGS.value,
          element: (
            <RecaptchaProvider>
              <Pages.UserSettingsPage />
            </RecaptchaProvider>
          ),
          loader: routeLoader(guards.passPlanHolder, null)
        },
        {
          path: PathConstants.Routes.USER_SPACE.value,
          element: <Pages.UserSpacePage />,
          loader: routeLoader(null, () => Loaders.loadUserSpace(account))
        },
        {
          path: PathConstants.Routes.USER_ENROLLMENTS_V2.value,
          element: <Pages.UserEnrollmentsPage />,
          loader: routeLoader(null, () => Loaders.loadUserEnrollments())
        },
        {
          path: PathConstants.Routes.USER_ENROLLMENT_DETAIL.value,
          element: <Pages.UserEnrollmentDetailPage />,
          loader: routeLoader(null, ({ params }: LoaderContext) =>
            Loaders.loadUserEnrollmentDetail(params!.enrollmentId!)
          )
        },
        {
          path: PathConstants.Routes.FAVORITES.value,
          id: PathConstants.Routes.FAVORITES.name,
          element: <Pages.FavoritesPage />,
          loader: routeLoader(null, null)
        }
      ]
    },

    // ========================================
    // USER ENROLLMENT ENGAGEMENT ROUTE
    // Layout: BasicLayout (logo + language selector header)
    // ========================================
    {
      element: <BasicLayout footerVariant="compact" />,
      children: [
        {
          path: PathConstants.Routes.USER_ENROLLMENT_ENGAGEMENT.value,
          element: <Pages.UserEnrollmentEngagementPage />,
          loader: routeLoader(null, ({ params }: LoaderContext) =>
            Loaders.loadUserEnrollmentEngagement(params!.enrollmentId!)
          )
        }
      ]
    },

    // ========================================
    // CONTENT DISCOVERY ROUTES
    // Layout: LoggedLayout (navMenu="main", footerVariant="full", enableSearch=true)
    // ========================================
    {
      element: <LoggedLayout navMenu="main" footerVariant="full" enableSearch={true} />,
      children: [
        {
          path: PathConstants.Routes.SEARCH.value,
          id: PathConstants.Routes.SEARCH.name,
          element: <Pages.SearchPage />,
          loader: routeLoader(guards.passPlanHolder, ({ request }: LoaderContext) =>
            Loaders.loadContentSearch({
              searchParams: new URL(request!.url).searchParams,
              account: account
            })
          )
        },
        {
          path: PathConstants.Routes.SKILLS.value,
          id: PathConstants.Routes.SKILLS.name,
          element: <Pages.SkillSearchPage />,
          loader: routeLoader(guards.passPlanHolder, ({ request, params }: LoaderContext) => {
            const userSkillId = params!.id ? parseInt(params!.id) : undefined
            return Loaders.loadContentSearch({
              searchParams: new URL(request!.url).searchParams,
              account: account,
              userSkillId
            })
          })
        },
        {
          path: PathConstants.Routes.INSTITUTIONS.value,
          id: PathConstants.Routes.INSTITUTIONS.name,
          element: <Pages.InstitutionsListPartnersPage />,
          loader: routeLoader(guards.passPlanHolder, ({ request }: LoaderContext) =>
            Loaders.loadInstitutionsSearch(new URL(request!.url).searchParams)
          )
        },
        {
          path: PathConstants.Routes.INSTITUTION.value,
          id: PathConstants.Routes.INSTITUTION.name,
          element: <Pages.InstitutionSearchPartnerPage />,
          loader: routeLoader(guards.passPlanHolder, ({ request, params }: LoaderContext) =>
            Loaders.loadContentSearch({
              searchParams: new URL(request!.url).searchParams,
              account: account,
              institutionId: params?.id ? parseInt(params.id) : undefined
            })
          )
        },
        ...Category.getAvailables().map(category => ({
          path: PathConstants.getCategoryRoutePath(category),
          id: category.name.toLocaleLowerCase(),
          element: <Pages.CategorySearchPage category={category} />,
          loader: routeLoader(guards.passPlanHolder, ({ request }: LoaderContext) =>
            Loaders.loadContentSearch({
              searchParams: new URL(request!.url).searchParams,
              account: account,
              categories: [category]
            })
          )
        })),
        ...EnrollmentCategory.getGroups().map(group => ({
          path: PathConstants.getCategoryGroupRoutePath(group),
          id: group.name.toLocaleLowerCase(),
          element: <Pages.CategoryGroupSearchPage group={group} />,
          loader: routeLoader(guards.passPlanHolder, ({ request }: LoaderContext) =>
            Loaders.loadContentSearch({
              searchParams: new URL(request!.url).searchParams,
              account: account,
              categories: group.categories
            })
          )
        })),
        {
          path: PathConstants.Routes.CONTENT.value,
          id: PathConstants.Routes.CONTENT.name,
          element: <Pages.ContentPage />,
          loader: routeLoader(guards.passPlanHolder, ({ params }: LoaderContext) => {
            const id = parseInt(params!.id ?? '')
            if (isNaN(id) || id <= 0) return null
            return Loaders.loadContentDetails(id)
          })
        },
        {
          path: PathConstants.Routes.REFUND.value,
          element: <Pages.RefundProgramListPage />,
          loader: routeLoader(guards.passPlanHolder, () => Loaders.loadRefundProgramList())
        }
      ]
    },

    // ========================================
    // COMPANY MANAGER ROUTES
    // Layout: LoggedLayout (navMenu="companyManager", footerVariant="compact", enableSearch=false)
    // ========================================
    {
      element: (
        <LoggedLayout navMenu="companyManager" footerVariant="compact" enableSearch={false} />
      ),
      children: [
        {
          path: PathConstants.Routes.COMPANY_MANAGER_DASHBOARD.value,
          element: <Pages.DashboardPage />,
          loader: routeLoader(guards.companyManager, () => Loaders.loadCompanyManagerDashboard())
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_CREATE_USER_PROFILE.value,
          element: <Pages.CreateUserProfilePage />,
          loader: routeLoader(guards.anyCompanyManager, () =>
            Loaders.loadCompanyManagerCreateUserProfile(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_EDIT_USER_PROFILE.value,
          element: <Pages.EditUserProfilePage />,
          loader: routeLoader(guards.anyCompanyManager, ({ params }: LoaderContext) =>
            Loaders.loadCompanyManagerUserProfile(params!.accountId!, account.id)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_EDIT_DEPENDENT_USER_PROFILE.value,
          element: <Pages.EditUserProfilePage />,
          loader: routeLoader(guards.anyCompanyManager, ({ params }: LoaderContext) =>
            Loaders.loadCompanyManagerUserProfile(params!.accountId!, account.id)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_DEPENDENT_LIST.value,
          element: <Pages.DependentSearchPage />,
          loader: routeLoader(guards.companyManager, ({ request }: LoaderContext) =>
            Loaders.loadCompanyManagerDependentSearch(request!)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_HOLDER_SEARCH.value,
          element: <Pages.DependentHolderSearchPage accountProfile={account} />,
          loader: routeLoader(guards.companyManager, () =>
            Loaders.loadCompanyManagerDependentLicenses(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_DEPENDENT_REGISTRATION.value,
          element: <Pages.DependentRegistrationPage />,
          loader: routeLoader(guards.companyManager, ({ params }: LoaderContext) =>
            Loaders.loadCompanyManagerDependentRegistration(params!.accountId!)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_REQUESTS_CONFIGURATION.value,
          element: <Pages.RequestsConfigurationPage />,
          loader: routeLoader(guards.companyManager, () =>
            Loaders.loadCompanyManagerDependentConfiguration(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_DEPENDENT_ENROLLMENT.value,
          element: <Pages.DependentEnrollmentPage />,
          loader: routeLoader(guards.companyManager, () =>
            Loaders.loadCompanyManagerDependentConfiguration(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_UPGRADE_ENROLLMENT.value,
          element: <Pages.UpgradeEnrollmentPage />,
          loader: routeLoader(guards.companyManager, () =>
            Loaders.loadCompanyManagerUpgradeRequest(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_AUTHORIZATIONS.value,
          element: <Pages.AuthorizationPage />,
          loader: routeLoader(guards.anyCompanyManager, () =>
            Loaders.loadCompanyManagerAuthorizations(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_AUTHORIZATION_DETAIL.value,
          element: <Pages.AuthorizationDetailPage />,
          loader: routeLoader(guards.anyCompanyManager, ({ params }: LoaderContext) =>
            Loaders.loadAuthorizationDetail(params!.authorizationId!)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_COLLABORATORS.value,
          element: <Pages.CollaboratorsPage />,
          loader: routeLoader(guards.anyCompanyManager, () =>
            Loaders.loadCompanyManagerCollaborators(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_COLLABORATORS_IMPORT.value,
          element: <Pages.CollaboratorsImportPage />,
          loader: routeLoader(guards.anyCompanyManager, () =>
            Loaders.loadCompanyManagerCollaboratorsImport(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_GROUPS.value,
          element: <Pages.GroupsPage />,
          loader: routeLoader(guards.anyCompanyManager, () =>
            Loaders.loadCompanyManagerGroups(account)
          )
        }
      ]
    },

    // ========================================
    // INTERNAL COMPANY MANAGER ROUTES
    // Layout: Internal Company Manager navigation, compact footer, no search
    // ========================================
    {
      element: (
        <LoggedLayout
          navMenu="internalCompanyManager"
          footerVariant="compact"
          enableSearch={false}
        />
      ),
      children: [
        {
          path: PathConstants.Routes.INTERNAL_COMPANY_MANAGER_USER_BATCH_DISABLE.value,
          element: <Pages.BatchDisableUserPage />,
          loader: routeLoader(guards.internalCompanyManager, null)
        },
        {
          path: PathConstants.Routes.INTERNAL_COMPANY_MANAGER_INVOICE.value,
          element: <Pages.InvoicePage />,
          loader: routeLoader(guards.internalCompanyManager, ({ request }: LoaderContext) =>
            Loaders.loadInvoiceList(request!)
          )
        },
        {
          path: PathConstants.Routes.INTERNAL_COMPANY_MANAGER_INVOICE_DETAIL.value,
          element: <Pages.InvoiceDetailPage />,
          loader: routeLoader(guards.internalCompanyManager, ({ params }: LoaderContext) =>
            Loaders.loadInvoiceDetail(params!.id!)
          )
        },
        {
          path: PathConstants.Routes.INTERNAL_COMPANY_MANAGER_COMPANY_DETAIL.value,
          element: <Pages.CompanyDetailPage />,
          loader: routeLoader(guards.internalCompanyManager, ({ params }: LoaderContext) =>
            Loaders.loadCompanyDetail(params!.id!)
          )
        },
        {
          path: PathConstants.Routes.INTERNAL_COMPANY_MANAGER_PARTNER_DETAIL.value,
          element: <Pages.PartnerDetailPage />,
          loader: routeLoader(guards.internalCompanyManager, ({ params }: LoaderContext) =>
            Loaders.loadPartnerDetail(params!.id!)
          )
        }
      ]
    },

    // ========================================
    // ACTION ROUTES
    // Layout: Basic layout, compact footer
    // Routes for specific user actions (enrollment, requests, assessments, etc.)
    // ========================================
    {
      element: <BasicLayout footerVariant="compact" />,
      children: [
        {
          path: PathConstants.Routes.CONTENT_ENROLLMENT.value,
          element: <Pages.ContentEnrollmentPage />,
          loader: routeLoader(guards.passPlanHolder, ({ params }: LoaderContext) =>
            Loaders.loadContentEnrollment(parseInt(params!.id!))
          )
        },
        {
          path: PathConstants.Routes.CONTENT_UNSUBSCRIBE.value,
          element: <Pages.ContentUnsubscribePage />,
          loader: routeLoader(guards.passPlanHolder, ({ params }: LoaderContext) =>
            Loaders.loadContentUnsubscribe(params!.id!)
          )
        },
        {
          path: PathConstants.Routes.CONTENT_COMPLETE.value,
          element: <Pages.ContentCompletePage />,
          loader: routeLoader(null, ({ params }: LoaderContext) =>
            Loaders.loadContentCompletion(params!.id!)
          )
        },
        {
          path: PathConstants.Routes.CBU_START.value,
          element: <Pages.CbUnicoStartPage />,
          loader: routeLoader(null, null)
        },
        {
          path: PathConstants.Routes.PROFILE_ASSESSMENT_CALLBACK.value,
          element: <Pages.ProfileAssessmentCallbackPage />,
          loader: routeLoader(null, null)
        },
        {
          path: PathConstants.Routes.PROFILE_ASSESSMENT_IN_PROGRESS.value,
          element: <Pages.ProfileAssessmentInProgressPage />,
          loader: routeLoader(null, null)
        },
        {
          path: PathConstants.Routes.PROFILE_ASSESSMENT_GET_STARTED.value,
          element: <Pages.ProfileAssessmentGetStartedPage />,
          loader: routeLoader(null, null)
        },
        {
          path: PathConstants.Routes.REFUND_REQUEST.value,
          element: <Pages.RefundRequestPage />,
          loader: routeLoader(guards.passPlanHolder, ({ params }: LoaderContext) =>
            Loaders.loadRefundRequest(params!.id!)
          )
        },
        {
          path: PathConstants.Routes.UPGRADE_REQUEST.value,
          element: <Pages.UpgradeRequestPage />,
          loader: routeLoader(guards.passPlanHolder, () => Loaders.loadUpgradeRequest(account))
        },
        {
          path: PathConstants.Routes.DEPENDENT_REQUEST.value,
          element: <Pages.DependentRequestPage />,
          loader: routeLoader(guards.passPlanHolder, () => Loaders.loadDependentRequest(account))
        },
        {
          path: PathConstants.Routes.DEPENDENT_REQUEST_CATALOG.value,
          element: <Pages.DependentRequestCatalogPage />,
          loader: routeLoader(guards.passPlanHolder, ({ request }: LoaderContext) =>
            Loaders.loadDependentCatalog(request!, account)
          )
        },
        {
          path: PathConstants.Routes.DEPENDENT_REQUEST_CONTENT.value,
          id: PathConstants.Routes.DEPENDENT_REQUEST_CONTENT.name,
          element: <Pages.DependentContentPage />,
          loader: routeLoader(guards.passPlanHolder, ({ params }: LoaderContext) =>
            Loaders.loadDependentContent(parseInt(params!.id!))
          )
        },
        {
          path: PathConstants.Routes.CONTACT_VERIFICATION.value,
          element: <Pages.ContactVerificationPage />,
          loader: routeLoader(null, () => Loaders.contactVerificationLoader())
        }
      ]
    }
  ]

  return [...routes]
}
